import { useEffect } from "react";
import { TEXT, BLOCKS, norm } from "./es";

type Saved = { el: Node; kind: "block" | "attr"; value: any };
const saved: Saved[] = [];
const texts = new Map<Node, string>();
const done = new WeakSet<Node>();
const BLOCK_TAGS = new Set(["P", "LI", "H1", "H2", "H3"]);
const INLINE = new Set(["B", "STRONG", "I", "EM", "A", "BR", "FONT", "SPAN"]);

let ORG_SUB = "";
export const setOrgSub = (s: string) => { ORG_SUB = s; };
const DIOCESE = ["The Catholic Diocese of Wilmington", "the Catholic Diocese of Wilmington", "The Diocese of Wilmington", "the Diocese of Wilmington"];

const SHORT = Object.keys(BLOCKS).filter((k) => k.length < 50);
const lookup = (t: string): string | undefined => BLOCKS[t.slice(0, 50)] || BLOCKS[SHORT.find((k) => t.startsWith(k)) || ""];

function orgBlock(t: string): string | undefined {
  if (!ORG_SUB || !t.includes(ORG_SUB)) return undefined;
  for (const d of DIOCESE) {
    const v = lookup(norm(t.split(ORG_SUB).join(d)));
    if (v) return v.replace(/(?:[Ll]a )?Diócesis (?:Católica )?de Wilmington/g, ORG_SUB);
  }
  return undefined;
}

function blockText(el: Element): string | null {
  const t = norm(el.textContent || "");
  if (t.length < 40) return null;
  const hit = lookup(t);
  const v = hit ? (ORG_SUB ? hit.replace(/(?:[Ll]a )?Diócesis (?:Católica )?de Wilmington/g, ORG_SUB) : hit) : orgBlock(t);
  if (!v) return null;
  const money = t.match(/\$\d[\d,]*\.\d{2}/)?.[0] || "";
  const org = t.match(/participation with (.+?), and the fee/)?.[1] || "";
  return v.replace("{money}", money).replace("{org}", org);
}

function translateNode(root: Node) {
  if (root.nodeType === Node.TEXT_NODE) return translateText(root as Text);
  if (!(root instanceof Element)) return;
  const els = [root, ...Array.from(root.querySelectorAll("*"))];
  for (const el of els) {
    if (BLOCK_TAGS.has(el.tagName) && !done.has(el) && Array.from(el.children).every((c) => INLINE.has(c.tagName))) {
      const v = blockText(el);
      if (v) {
        saved.push({ el, kind: "block", value: Array.from(el.childNodes) });
        done.add(el);
        el.replaceChildren(document.createTextNode(v));
        continue;
      }
    }
    const ph = el.getAttribute("placeholder");
    if (ph && TEXT[ph]) { saved.push({ el, kind: "attr", value: ph }); el.setAttribute("placeholder", TEXT[ph]); }
    el.childNodes.forEach((n) => n.nodeType === Node.TEXT_NODE && translateText(n as Text));
  }
}

function translateText(n: Text) {
  const raw = n.nodeValue || "";
  const v = TEXT[norm(raw)] || dynamic(norm(raw));
  if (!v) return;
  texts.set(n, raw);
  n.nodeValue = raw.replace(norm(raw), v);
}

function dynamic(s: string): string | undefined {
  const m = s.match(/^(.+) is required\.$/);
  if (m) return `${TEXT[m[1]] || m[1]} es obligatorio.`;
  return undefined;
}

function restore() {
  texts.forEach((v, n) => { (n as Text).nodeValue = v; });
  texts.clear();
  while (saved.length) {
    const s = saved.pop()!;
    if (s.kind === "attr") (s.el as Element).setAttribute("placeholder", s.value);
    else { (s.el as Element).replaceChildren(...s.value); done.delete(s.el); }
  }
}

export function useSpanish(on: boolean) {
  useEffect(() => {
    if (!on) return;
    document.documentElement.lang = "es";
    translateNode(document.body);
    const obs = new MutationObserver((muts) => {
      obs.disconnect();
      muts.forEach((m) => (m.type === "characterData" ? translateNode(m.target) : m.addedNodes.forEach(translateNode)));
      obs.observe(document.body, { subtree: true, childList: true, characterData: true });
    });
    obs.observe(document.body, { subtree: true, childList: true, characterData: true });
    return () => { obs.disconnect(); restore(); document.documentElement.lang = "en"; };
  }, [on]);
}
